import type { SerializeFrom } from '~/types/serialize-from';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { atom, useAtom } from 'jotai';
import { useActivitySensor } from '@archipro-website/react-hooks';
import {
    CONVERSATIONS_API,
    type loader,
} from '~/routes/remix-api.chat.conversations';
import type { Conversation } from '../types';
import { useConversationCache } from './use-conversation-cache';
import { useRealTimeChat } from './use-real-time-chat';

type ConversationsResult = NonNullable<SerializeFrom<typeof loader>>;
type PageInfo = ConversationsResult['pageInfo'];

const conversationsStateAtom = atom<{
    professionalID: number | null;
    conversations: Conversation[];
    pageInfo: PageInfo | null;
    offset: number;
} | null>(null);

/**
 * Hook to get a paginated list of conversations for the current user.
 * @param limit The maximum number of conversations to return. Do not change this
 *  value. Defaults to 20.
 */
export function useConversations({
    professionalID,
    limit = 20,
}: {
    /**
     * The professional ID to scope the conversations to a company inbox. If `null`,
     * the personal inbox will be used.
     */
    professionalID: number | null;
    limit?: number;
}) {
    const { lastCachedConversation } = useConversationCache();
    const pageActive = useActivitySensor();
    const { isConnected } = useRealTimeChat();
    const isFetched = useRef(false);
    const processedDataRef = useRef<typeof fetcher.data | null>(null);
    const [search, setSearch] = useState('');
    const [buildHubIDs, setBuildHubIDs] = useState<string[]>([]);
    const [isArchived, setIsArchived] = useState(false);
    const [branchProfessionalID, setBranchProfessionalID] = useState<
        number | null
    >(null);
    const [conversationsState, setConversationsState] = useAtom(
        conversationsStateAtom,
    );
    const fetcher = useFetcher<typeof loader>();
    const currentConversationsState = useMemo(() => {
        if (!conversationsState) return null;
        return conversationsState.professionalID === professionalID
            ? conversationsState
            : null;
    }, [conversationsState, professionalID]);
    const conversations = currentConversationsState?.conversations ?? null;
    const pageInfo = currentConversationsState?.pageInfo ?? null;
    const offset = currentConversationsState?.offset ?? 0;

    const buildApiUrl = useCallback(
        (offset: number) => {
            const url = new URL(CONVERSATIONS_API, window.location.origin);
            if (professionalID && professionalID > 0) {
                url.searchParams.set('professionalID', String(professionalID));
            }
            if (professionalID && professionalID > 0 && branchProfessionalID) {
                url.searchParams.set(
                    'branchProfessionalID',
                    String(branchProfessionalID),
                );
            }
            url.searchParams.set('q', search);
            url.searchParams.set('isArchived', isArchived ? '1' : '0');
            url.searchParams.set('limit', String(limit));
            url.searchParams.set('offset', String(offset));
            for (const buildHubID of buildHubIDs) {
                url.searchParams.append('buildHubIDs', buildHubID);
            }
            return `${url.pathname}${url.search}`;
        },
        [
            professionalID,
            branchProfessionalID,
            search,
            isArchived,
            limit,
            buildHubIDs,
        ],
    );

    const apiUrl = useMemo(() => {
        return buildApiUrl(offset);
    }, [offset, buildApiUrl]);

    const setBranch = useCallback(
        (nextBranchProfessionalID: number | null) => {
            setBranchProfessionalID(nextBranchProfessionalID);
            setConversationsState((prev) =>
                prev?.professionalID === professionalID
                    ? { ...prev, offset: 0 }
                    : prev,
            );
        },
        [professionalID, setConversationsState],
    );

    const setParams = useCallback(
        (params: {
            search?: string;
            buildHubIDs?: string[];
            isArchived?: boolean;
        }) => {
            if (params.search !== undefined) {
                setSearch(params.search.trim());
            }
            if (params.buildHubIDs !== undefined) {
                setBuildHubIDs(params.buildHubIDs);
            }
            if (params.isArchived !== undefined) {
                setIsArchived(params.isArchived);
            }
            setConversationsState((prev) =>
                prev?.professionalID === professionalID
                    ? { ...prev, offset: 0 }
                    : prev,
            );
        },
        [professionalID, setConversationsState],
    );

    const resetParams = useCallback(() => {
        setParams({ search: '', buildHubIDs: [], isArchived: false });
    }, [setParams]);

    const refetch = useCallback(() => {
        setConversationsState((prev) =>
            prev?.professionalID === professionalID
                ? { ...prev, offset: 0 }
                : prev,
        );
        fetcher.load(buildApiUrl(0));
    }, [fetcher, professionalID, buildApiUrl, setConversationsState]);

    const loadMore = useCallback(() => {
        if (
            fetcher.state !== 'idle' ||
            !conversations ||
            !pageInfo?.hasNextPage
        ) {
            return;
        }
        setConversationsState((prev) =>
            prev?.professionalID === professionalID
                ? { ...prev, offset: prev.conversations.length }
                : prev,
        );
    }, [
        fetcher.state,
        conversations,
        pageInfo,
        professionalID,
        setConversationsState,
    ]);

    // Reset the branch professional ID when the professional ID changes.
    useEffect(() => {
        setBranchProfessionalID(null);
    }, [professionalID]);

    // Fetch initial conversations on mount.
    useEffect(() => {
        if (fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (isFetched.current) {
            // Refetch when the query params change.
            fetcher.load(apiUrl);
        }
        // Must not include `fetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    // Set data from the fetcher.
    useEffect(() => {
        if (
            !fetcher.data ||
            fetcher.state !== 'idle' ||
            fetcher.data === processedDataRef.current
        ) {
            return;
        }
        const newConversations = fetcher.data.conversations;
        const newPageInfo = fetcher.data.pageInfo;
        setConversationsState((prev) => {
            if (
                !prev ||
                prev.professionalID !== professionalID ||
                !newPageInfo.hasPreviousPage
            ) {
                return {
                    professionalID,
                    conversations: newConversations,
                    pageInfo: newPageInfo,
                    offset,
                };
            }
            const next = [...prev.conversations];
            for (const conversation of newConversations) {
                if (!prev.conversations.some((c) => c.ID === conversation.ID)) {
                    next.push(conversation);
                }
            }
            return {
                professionalID,
                conversations: next,
                pageInfo: newPageInfo,
                offset,
            };
        });
        processedDataRef.current = fetcher.data;
    }, [fetcher, professionalID, offset, setConversationsState]);

    // Update the chat settings and unread message count as soon as possible
    // from the conversation cache.
    useEffect(() => {
        if (
            !lastCachedConversation ||
            lastCachedConversation.professionalID !== professionalID
        ) {
            return;
        }
        const cachedConversation = lastCachedConversation.conversation;
        setConversationsState((prev) => {
            if (
                prev?.conversations.some(
                    (c) => c.ID === String(cachedConversation.ID),
                )
            ) {
                const next = prev.conversations.map((c) =>
                    c.ID === String(cachedConversation.ID)
                        ? {
                              ...c,
                              UnreadMessageCount:
                                  cachedConversation.UnreadMessageCount,
                              IsArchived: cachedConversation.IsArchived,
                              IsMuted: cachedConversation.IsMuted,
                              ...(cachedConversation.__typename ===
                              'DirectConversation'
                                  ? {
                                        Name: cachedConversation.Name,
                                        BuildHub: cachedConversation.BuildHub,
                                        Thumbnail: cachedConversation.Thumbnail,
                                        IsArchived:
                                            cachedConversation.IsArchived,
                                    }
                                  : {}),
                          }
                        : c,
                );
                return { ...prev, conversations: next };
            }
            return prev;
        });
    }, [lastCachedConversation, professionalID, setConversationsState]);

    // Poll every 5 minutes if the websocket is connected or the page is
    // inactive; fall back to 30 seconds when the websocket is disconnected
    // and the user is active.
    useEffect(() => {
        const pollingIntervalMs = isConnected || !pageActive ? 300_000 : 30_000;
        const interval = setInterval(() => {
            refetch();
        }, pollingIntervalMs);
        return () => clearInterval(interval);
    }, [isConnected, pageActive, refetch]);

    return {
        /**
         * The list of conversations.
         */
        conversations,
        /**
         * There are more conversations to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * The conversations are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * The selected branch professional ID, or `null` for all linked profiles.
         */
        branchProfessionalID,
        /**
         * Set the branch filter for the company inbox.
         */
        setBranch,
        /**
         * Set the filter parameters for the `GetConversations` query. Parameters
         * that are `undefined` will not be changed.
         */
        setParams,
        /**
         * Reset the filter parameters to the default values.
         */
        resetParams,
        /**
         * Refetch the conversations.
         */
        refetch,
        /**
         * Load more conversations by fetching the next page. This increments the
         * offset and appends the new results to the existing conversations.
         */
        loadMore,
    };
}

/**
 * Hook to manage the conversations from the useConversations hook. Use this
 * hook to manipulate the conversations data with fetching from the server.
 */
export function useConversationsState() {
    const [conversationsState, setConversationsState] = useAtom(
        conversationsStateAtom,
    );

    const removeConversation = useCallback(
        (conversationID: number, professionalID: number | null) => {
            setConversationsState((prev) => {
                if (!prev || prev.professionalID !== professionalID) {
                    return prev;
                }
                return {
                    ...prev,
                    conversations: prev.conversations.filter(
                        (c) => c.ID !== String(conversationID),
                    ),
                };
            });
        },
        [setConversationsState],
    );

    return {
        conversationsState,
        /**
         * Remove a conversation from the list of conversations.
         */
        removeConversation,
    };
}
